import type { ManagementPermission } from "./permissions";

/**
 * 控制台页面标识。标识只描述产品页面，不包含 URL 或应用上下文，便于菜单、
 * 权限守卫和页面标题共享同一份定义。
 */
export type ConsoleView =
  | "applications"
  | "app-overview"
  | "app-settings"
  | "credentials"
  | "quickstart"
  | "users"
  | "system-accounts"
  | "groups"
  | "message-send"
  | "server-messages"
  | "callbacks"
  | "callback-failures"
  | "retention"
  | "not-found";

export interface ConsoleRoute {
  readonly view: ConsoleView;
  readonly appId: string | null;
}

const applicationRoutes: Readonly<Record<string, ConsoleView>> = {
  overview: "app-overview",
  "settings/general": "app-settings",
  "settings/credentials": "credentials",
  integration: "quickstart",
  users: "users",
  "system-accounts": "system-accounts",
  groups: "groups",
  "messages/send": "message-send",
  "messages/logs": "server-messages",
  "callbacks/config": "callbacks",
  "callbacks/deliveries": "callback-failures",
  "data/retention": "retention",
};

const routeSuffixes = Object.fromEntries(
  Object.entries(applicationRoutes).map(([suffix, view]) => [view, suffix]),
) as Readonly<Partial<Record<ConsoleView, string>>>;

const permissions: Readonly<Partial<Record<ConsoleView, ManagementPermission>>> = {
  users: "im.user.read",
  "system-accounts": "im.system_account.read",
  groups: "im.group.read",
  "message-send": "im.system_account.send",
  "server-messages": "im.message.read",
  callbacks: "im.callback.read",
  "callback-failures": "im.callback_failure_log.read",
  retention: "im.retention.read",
};

/** 将浏览器路径解析为稳定、可校验的控制台路由。 */
export function parseConsoleRoute(pathname: string): ConsoleRoute {
  const normalized = normalizePath(pathname);
  if (normalized === "/" || normalized === "/overview") {
    return { view: "applications", appId: null };
  }
  if (normalized === "/applications") {
    return { view: "applications", appId: null };
  }

  const match = /^\/applications\/([^/]+)(?:\/(.*))?$/u.exec(normalized);
  if (match === null) return { view: "not-found", appId: null };
  const appId = decodePathSegment(match[1] ?? "");
  const suffix = match[2] ?? "overview";
  const view = applicationRoutes[suffix];
  if (appId === null || view === undefined) return { view: "not-found", appId: null };
  return { view, appId };
}

/** 为页面和应用上下文生成可收藏、可分享的绝对路径。 */
export function consoleRoutePath(view: ConsoleView, appId: string | null = null): string {
  if (view === "applications") return "/applications";
  if (view === "not-found") return "/404";
  const suffix = routeSuffixes[view];
  if (suffix === undefined || appId === null || appId.length === 0) return "/applications";
  return `/applications/${encodeURIComponent(appId)}/${suffix}`;
}

/** 返回进入页面所需的应用级权限；无返回值表示仅要求应用所有权。 */
export function requiredPermission(view: ConsoleView): ManagementPermission | undefined {
  return permissions[view];
}

/** 判断页面是否必须在一个明确的应用上下文中打开。 */
export function isApplicationScoped(view: ConsoleView): boolean {
  return view !== "applications" && view !== "not-found";
}

/**
 * 返回已下线全局总览入口的兼容跳转地址。旧书签继续可用，但地址栏会立即
 * 归一化到应用中心，避免控制台同时维护两个含义相近的全局入口。
 */
export function legacyConsoleRouteRedirect(pathname: string): string | null {
  const normalized = normalizePath(pathname);
  return normalized === "/" || normalized === "/overview" ? "/applications" : null;
}

function normalizePath(pathname: string): string {
  if (pathname === "") return "/";
  const withoutTrailingSlash = pathname.length > 1 ? pathname.replace(/\/+$/u, "") : pathname;
  return withoutTrailingSlash.startsWith("/") ? withoutTrailingSlash : `/${withoutTrailingSlash}`;
}

function decodePathSegment(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return decoded.length === 0 || decoded.includes("/") ? null : decoded;
  } catch {
    return null;
  }
}
